import * as THREE from "three";
import type { Animal } from "../entities/animals";

/**
 * Blocky procedural models for passive animals.
 *
 * Every model is built from the same unit cube, scaled into body parts. The
 * materials are per-animal clones so a hit can flash one mob red without
 * tinting the rest of the herd, while the shared geometry keeps the extra draw
 * calls cheap.
 */

interface Leg {
  readonly pivot: THREE.Group;
  readonly offset: number;
}

interface MaterialBase {
  readonly material: THREE.MeshBasicMaterial;
  readonly base: THREE.Color;
}

interface Visual {
  readonly group: THREE.Group;
  readonly legs: Leg[];
  readonly materials: MaterialBase[];
  readonly phase: number;
}

const COW_WHITE = 0xe7e3da;
const COW_PATCH = 0x2d2926;
const COW_SNOUT = 0xd7a49d;
const COW_HORN = 0xd9cba5;

const PIG_BODY = 0xe89c9c;
const PIG_DARK = 0xcd7c7c;
const PIG_SNOUT = 0xd77f7f;

const SHEEP_WOOL = 0xf0ede4;
const SHEEP_FACE = 0xd5c3b2;
const SHEEP_LEG = 0x8d8378;

const EYE = 0x151515;
const FLASH = new THREE.Color(0xff4a3d);

export class AnimalRenderer {
  readonly group = new THREE.Group();

  private readonly boxGeometry = new THREE.BoxGeometry(1, 1, 1);
  private readonly visuals = new Map<number, Visual>();

  constructor(parent: THREE.Object3D) {
    this.group.name = "animals";
    parent.add(this.group);
  }

  /** Mirror the simulation into the scene, creating/removing models as needed. */
  sync(animals: readonly Animal[], light: number): void {
    const seen = new Set<number>();
    const brightness = Math.max(0, light);

    for (const animal of animals) {
      seen.add(animal.id);
      let visual = this.visuals.get(animal.id);
      if (!visual) {
        visual = this.createVisual(animal);
        this.visuals.set(animal.id, visual);
        this.group.add(visual.group);
      }

      const speed = Math.hypot(animal.velocity.x, animal.velocity.z);
      const walk = Math.min(1, speed / 2);
      const phase = animal.age * 9 + visual.phase;
      visual.group.position.set(
        animal.position.x,
        animal.position.y + Math.abs(Math.sin(phase)) * 0.035 * walk,
        animal.position.z,
      );
      visual.group.rotation.y = animal.yaw;

      for (const leg of visual.legs) {
        leg.pivot.rotation.x = Math.sin(phase + leg.offset) * 0.42 * walk;
      }

      const flash = animal.hurtFlash > 0 && Math.sin(animal.age * 55) > -0.25 ? 0.68 : 0;
      for (const entry of visual.materials) {
        entry.material.color.copy(entry.base).multiplyScalar(brightness);
        if (flash > 0) entry.material.color.lerp(FLASH, flash);
      }
    }

    for (const [id, visual] of this.visuals) {
      if (seen.has(id)) continue;
      this.disposeVisual(visual);
      this.visuals.delete(id);
    }
  }

  dispose(): void {
    for (const visual of this.visuals.values()) this.disposeVisual(visual);
    this.visuals.clear();
    this.group.removeFromParent();
    this.boxGeometry.dispose();
  }

  private createVisual(animal: Animal): Visual {
    const group = new THREE.Group();
    const materials: MaterialBase[] = [];
    const byColor = new Map<number, THREE.MeshBasicMaterial>();

    const material = (hex: number): THREE.MeshBasicMaterial => {
      const existing = byColor.get(hex);
      if (existing) return existing;
      const created = new THREE.MeshBasicMaterial({ color: hex, fog: true });
      byColor.set(hex, created);
      materials.push({ material: created, base: created.color.clone() });
      return created;
    };

    const box = (
      parent: THREE.Object3D,
      hex: number,
      x: number,
      y: number,
      z: number,
      sx: number,
      sy: number,
      sz: number,
    ): THREE.Mesh => {
      const mesh = new THREE.Mesh(this.boxGeometry, material(hex));
      mesh.position.set(x, y, z);
      mesh.scale.set(sx, sy, sz);
      parent.add(mesh);
      return mesh;
    };

    const legs: Leg[] = [];
    const leg = (
      hex: number,
      x: number,
      z: number,
      hipY: number,
      width: number,
      length: number,
      offset: number,
    ): void => {
      const pivot = new THREE.Group();
      pivot.position.set(x, hipY, z);
      group.add(pivot);
      const mesh = new THREE.Mesh(this.boxGeometry, material(hex));
      mesh.scale.set(width, length, width);
      mesh.position.y = -length / 2;
      pivot.add(mesh);
      legs.push({ pivot, offset });
    };

    switch (animal.kind) {
      case "cow":
        box(group, COW_WHITE, 0, 0.98, 0, 0.9, 0.74, 1.34);
        // Patches sit just proud of the white body.
        box(group, COW_PATCH, -0.452, 0.98, -0.18, 0.025, 0.46, 0.58);
        box(group, COW_PATCH, 0.452, 0.98, 0.22, 0.025, 0.4, 0.42);
        box(group, COW_PATCH, 0, 1.352, -0.24, 0.42, 0.02, 0.46);
        box(group, COW_WHITE, 0, 1.12, 0.82, 0.58, 0.56, 0.56);
        box(group, COW_SNOUT, 0, 1.02, 1.11, 0.34, 0.24, 0.18);
        box(group, EYE, -0.16, 1.21, 1.101, 0.08, 0.08, 0.02);
        box(group, EYE, 0.16, 1.21, 1.101, 0.08, 0.08, 0.02);
        box(group, COW_HORN, -0.24, 1.4, 0.8, 0.1, 0.18, 0.1);
        box(group, COW_HORN, 0.24, 1.4, 0.8, 0.1, 0.18, 0.1);
        box(group, COW_PATCH, 0, 1.0, -0.72, 0.12, 0.38, 0.1);
        leg(COW_WHITE, -0.29, -0.42, 0.62, 0.2, 0.62, 0);
        leg(COW_WHITE, 0.29, -0.42, 0.62, 0.2, 0.62, Math.PI);
        leg(COW_WHITE, -0.29, 0.42, 0.62, 0.2, 0.62, Math.PI);
        leg(COW_WHITE, 0.29, 0.42, 0.62, 0.2, 0.62, 0);
        break;

      case "pig":
        box(group, PIG_BODY, 0, 0.74, 0, 0.78, 0.58, 0.98);
        box(group, PIG_BODY, 0, 0.9, 0.62, 0.52, 0.5, 0.52);
        box(group, PIG_SNOUT, 0, 0.82, 0.91, 0.27, 0.19, 0.16);
        box(group, EYE, -0.14, 1.01, 0.881, 0.075, 0.075, 0.02);
        box(group, EYE, 0.14, 1.01, 0.881, 0.075, 0.075, 0.02);
        box(group, PIG_DARK, -0.19, 1.18, 0.55, 0.14, 0.12, 0.1);
        box(group, PIG_DARK, 0.19, 1.18, 0.55, 0.14, 0.12, 0.1);
        box(group, PIG_DARK, 0, 0.82, -0.54, 0.1, 0.16, 0.08);
        leg(PIG_BODY, -0.24, -0.32, 0.46, 0.17, 0.46, 0);
        leg(PIG_BODY, 0.24, -0.32, 0.46, 0.17, 0.46, Math.PI);
        leg(PIG_BODY, -0.24, 0.32, 0.46, 0.17, 0.46, Math.PI);
        leg(PIG_BODY, 0.24, 0.32, 0.46, 0.17, 0.46, 0);
        break;

      case "sheep":
        box(group, SHEEP_WOOL, 0, 1.0, 0, 0.96, 0.86, 1.24);
        box(group, SHEEP_WOOL, 0, 1.46, -0.04, 0.78, 0.14, 1.0);
        box(group, SHEEP_WOOL, -0.5, 1.04, 0, 0.04, 0.52, 0.82);
        box(group, SHEEP_WOOL, 0.5, 1.04, 0, 0.04, 0.52, 0.82);
        box(group, SHEEP_FACE, 0, 1.12, 0.76, 0.5, 0.5, 0.5);
        box(group, SHEEP_WOOL, 0, 1.4, 0.72, 0.52, 0.15, 0.45);
        box(group, EYE, -0.13, 1.19, 1.011, 0.075, 0.075, 0.02);
        box(group, EYE, 0.13, 1.19, 1.011, 0.075, 0.075, 0.02);
        leg(SHEEP_LEG, -0.3, -0.4, 0.56, 0.17, 0.56, 0);
        leg(SHEEP_LEG, 0.3, -0.4, 0.56, 0.17, 0.56, Math.PI);
        leg(SHEEP_LEG, -0.3, 0.4, 0.56, 0.17, 0.56, Math.PI);
        leg(SHEEP_LEG, 0.3, 0.4, 0.56, 0.17, 0.56, 0);
        break;

      default:
        assertNever(animal.kind);
    }

    return {
      group,
      legs,
      materials,
      phase: (animal.id % 7) * 0.9,
    };
  }

  private disposeVisual(visual: Visual): void {
    visual.group.removeFromParent();
    for (const entry of visual.materials) entry.material.dispose();
  }
}

function assertNever(value: never): never {
  throw new Error(`Unknown animal kind: ${String(value)}`);
}
